import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from '../../../constants/project-name-constants';
import { LogFilterModel } from '../../../models/audit/log-filter.model';
import { ColumnResponseModel } from '../../../models/responses/column-response.model';
import { PagedResponseModel } from '../../../models/responses/paged-response.model';
import { LogTableModel } from '../../../models/tables/audit/log-table.model';
import { LogService } from '../../../services/audit/log.service';
import { HeaderService } from '../../../services/header/header.service';
import { CompanyService } from '../../../services/management/company.service';
import { ToastHandlerService } from '../../../services/toast/toast-handler.service';

@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss'],
  standalone: false
})
export class LogsComponent implements OnInit {

  private readonly companyService = inject(CompanyService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly logService = inject(LogService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendant: boolean = false;
  changeFilters: boolean = false;
  columnSort: string | null = null;
  currentFilter: LogFilterModel | null = null;
  formLogs: FormGroup = new FormGroup({});
  generalData: PagedResponseModel<LogTableModel> = new PagedResponseModel<LogTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  nameWindow: string = 'Logs Transacciones';
  page: number = 1;
  projectName = ProjectName.projectName;

  ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.getColumnsForGeneralConsultation();
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.logService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general de logs.', error);
    }
  }

  clear() {
    this.validateForm();
    this.generalData = new PagedResponseModel<LogTableModel>();
    this.currentFilter = null;
    this.changeFilters = false;
  }

  async send() {
    if (this.formLogs.invalid) {
      this.showValidationMessages();
      return;
    }

    const formData = this.formLogs.value;

    const dateInitial = formData.dateInitial as Date;
    const dateFinal = formData.dateFinal as Date;
    if (dateFinal < dateInitial) {
      this.toastHandlerService.showToast('warning', 'La fecha final no puede ser menor a la fecha inicial.');
      return;
    }

    const logFilterModel = new LogFilterModel();
    logFilterModel.timeStamp = dateInitial;
    logFilterModel.timeStampFinal = dateFinal;
    logFilterModel.message = formData.message || '';
    logFilterModel.userName = formData.userName || '';
    logFilterModel.service = formData.service || '';
    logFilterModel.method = formData.method || '';

    this.currentFilter = logFilterModel;
    this.page = 1;
    this.columnSort = null;
    this.ascendant = false;

    await this.getLogsWithPagination();
  }

  async getLogsWithPagination() {
    if (!this.currentFilter) {
      this.toastHandlerService.showToast('warning', 'Debe aplicar filtros antes de consultar.');
      return;
    }

    try {
      this.generalData = await firstValueFrom(
        this.logService.getAllWithPagination(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.currentFilter
        )
      );

      if (this.generalData.items && this.generalData.items.length > 0) {
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast('info', 'No se encontraron datos.');
      }
    } catch (error) {
      console.error('Error al consultar los logs:', error);
    }
  }

  onPageChange(event: { page: number; pageSize: number; sortField: string | undefined; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getLogsWithPagination();
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      dateInitial: {
        required: 'El campo Fecha Ini. es obligatorio.'
      },
      dateFinal: {
        required: 'El campo Fecha Fin. es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formLogs.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    const currentDate = moment().startOf('day').toDate();
    this.formLogs = this.formBuilder.group({
      dateInitial: [currentDate, [Validators.required]],
      dateFinal: [currentDate, [Validators.required]],
      message: [''],
      userName: [''],
      service: [''],
      method: ['']
    });
  }

}
